/* ──────────────────────  RestaurantProfile.jsx ────────────────────── */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

import {
  Box, Card, CardContent, CardHeader, Typography, TextField, Button,
  Avatar, Badge, Stack, Divider, CircularProgress, InputLabel, FormControl,
  styled, createTheme, ThemeProvider,
} from '@mui/material';
import { Restaurant, PhotoCamera, Save } from '@mui/icons-material';

// === LUXURY THEME (shared) ===
const theme = createTheme({
  palette: {
    primary: { main: '#d4af37' },
    secondary: { main: '#b8932b' },
    background: { default: '#f8f8f8', paper: '#ffffff' },
    text: { primary: '#1a1a1a', secondary: '#555555' },
    success: { main: '#28a745' },
  },
  typography: {
    fontFamily: '"Inter", "Poppins", sans-serif',
    h5: { fontWeight: 700 },
    h6: { fontWeight: 700 },
    body2: { fontWeight: 500 },
    caption: { fontSize: '0.75rem' },
  },
  shape: { borderRadius: 24 },
});

// === GOLD TEXT ===
const GoldText = styled(Typography)(({ theme }) => ({
  background: 'linear-gradient(90deg, #d4af37, #b8932b)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  fontWeight: 800,
}));

// === LUXURY CARD (compact) ===
const LuxuryCard = styled(Card)(({ theme }) => ({
  background: '#ffffff',
  border: '1px solid rgba(212,175,55,0.3)',
  borderRadius: 24,
  padding: '20px 18px',
  boxShadow: '0 8px 20px rgba(0,0,0,0.07)',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 12px 28px rgba(0,0,0,0.1)',
    transform: 'translateY(-2px)',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0, left: 0, right: 0,
    height: 4,
    background: 'linear-gradient(90deg, #d4af37, #b8932b)',
    borderRadius: '24px 24px 0 0',
  },
}));

// === UPLOAD BOX (compact) ===
const UploadBox = styled(Box)(({ theme }) => ({
  border: '2px dashed rgba(212,175,55,0.4)',
  borderRadius: 16,
  padding: 16,
  textAlign: 'center',
  backgroundColor: '#fffdf0',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    borderColor: '#d4af37',
    backgroundColor: '#fffbe6',
  },
}));

// === SAVE BUTTON ===
const SaveButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.9rem',
  padding: '10px 20px',
  boxShadow: '0 4px 12px rgba(212,175,55,0.25)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: '0 6px 16px rgba(212,175,55,0.35)',
  },
}));

export default function RestaurantProfile() {
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [logo, setLogo] = useState(null);
  const [preview, setPreview] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const navigate = useNavigate();
  const token = localStorage.getItem('admin_token');
  const api = process.env.REACT_APP_API_URL;
  const restaurantName = localStorage.getItem('restaurant_name') || 'Your Restaurant';

  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
      return;
    }
    loadProfile();
  }, [token, navigate]);

  const loadProfile = async () => {
    try {
      const res = await axios.get(`${api}/admin-profile.php`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setName(String(res.data.name ?? ''));
      setAddress(String(res.data.address ?? ''));
      setPreview(res.data.logo_url ? `${api}/${res.data.logo_url}` : '');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogo(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  const saveProfile = async () => {
    const trimmedName = String(name).trim();
    if (!trimmedName) {
      alert('Restaurant name is required');
      return;
    }

    setSaving(true);
    const formData = new FormData();
    formData.append('name', trimmedName);
    formData.append('address', String(address));
    if (logo) formData.append('logo', logo);

    try {
      await axios.post(`${api}/admin-update-profile.php`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      await loadProfile();
    } catch (err) {
      alert(err.response?.data?.error || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          bgcolor: '#f8f8f8',
        }}
      >
        <CircularProgress size={48} color="primary" />
        <Typography variant="h6" mt={2} color="text.secondary">
          Loading profile...
        </Typography>
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ p: { xs: 1.5, md: 2 }, bgcolor: '#f8f8f8', minHeight: '100vh' }}>
        {/* Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2.5}>
          <GoldText variant="h5" sx={{ fontSize: '1.35rem' }}>
            Restaurant Profile
          </GoldText>
          <Badge badgeContent={1} color="error">
            <Restaurant sx={{ fontSize: 28, color: '#d4af37' }} />
          </Badge>
        </Stack>

        <LuxuryCard>
          <CardHeader
            avatar={
              <Avatar
                sx={{
                  bgcolor: '#fffdf0',
                  color: '#d4af37',
                  width: 44,
                  height: 44,
                  fontWeight: 700,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                }}
              >
                {name.charAt(0).toUpperCase() || '?'}
              </Avatar>
            }
            title={
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a1a', fontSize: '1.1rem' }}>
                {restaurantName}
              </Typography>
            }
            subheader={
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                Update your restaurant details
              </Typography>
            }
            sx={{ pb: 1 }}
          />

          <CardContent sx={{ pt: 1 }}>
            {/* Name */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel
                shrink
                htmlFor="name-input"
                sx={{ fontWeight: 600, color: '#1a1a1a', fontSize: '0.95rem' }}
              >
                Restaurant Name <span style={{ color: '#c62828' }}>*</span>
              </InputLabel>
              <TextField
                id="name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Patna Spice"
                variant="outlined"
                size="small"
                sx={{
                  mt: 1.5,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 12,
                    backgroundColor: '#fffdf0',
                    fontSize: '0.95rem',
                  },
                }}
              />
            </FormControl>

            {/* Address */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel
                shrink
                htmlFor="address-input"
                sx={{ fontWeight: 600, color: '#1a1a1a', fontSize: '0.95rem' }}
              >
                Address
              </InputLabel>
              <TextField
                id="address-input"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="Full address (optional)"
                multiline
                rows={2}
                variant="outlined"
                size="small"
                sx={{
                  mt: 1.5,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 12,
                    backgroundColor: '#fffdf0',
                    fontSize: '0.9rem',
                  },
                }}
              />
            </FormControl>

            {/* Logo Upload */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel shrink sx={{ fontWeight: 600, color: '#1a1a1a', fontSize: '0.95rem' }}>
                Logo
              </InputLabel>

              <UploadBox mt={1.5}>
                {preview ? (
                  <Box
                    component="img"
                    src={preview}
                    alt="Logo preview"
                    sx={{
                      width: 96,
                      height: 96,
                      borderRadius: 12,
                      objectFit: 'cover',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      mb: 1,
                    }}
                  />
                ) : (
                  <Box sx={{ color: '#999', mb: 1 }}>
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                      <circle cx="8.5" cy="8.5" r="1.5" />
                      <path d="M21 15l-5-5L5 21" />
                    </svg>
                  </Box>
                )}

                <input
                  accept="image/*"
                  type="file"
                  id="logo-upload"
                  style={{ display: 'none' }}
                  onChange={handleLogoChange}
                />
                <label htmlFor="logo-upload">
                  <Button
                    variant="contained"
                    component="span"
                    startIcon={<PhotoCamera />}
                    size="small"
                    sx={{
                      borderRadius: 12,
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      bgcolor: '#1976d2',
                      '&:hover': { bgcolor: '#1565c0' },
                    }}
                  >
                    Choose
                  </Button>
                </label>
              </UploadBox>

              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#777' }}>
                300×300px, PNG/JPG
              </Typography>
            </FormControl>

            <Divider sx={{ my: 2, borderColor: 'rgba(212,175,55,0.3)' }} />

            {/* Save Button */}
            <SaveButton
                  fullWidth
                  variant="contained"
                  color="success"
                  startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <Save />}
                  onClick={saveProfile}
                  disabled={saving}
                  sx={{
                    bgcolor: '#28a745',
                    '&:hover': { bgcolor: '#218838' },
                    py: 1.2,
                  }}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </SaveButton>
          </CardContent>
        </LuxuryCard>
      </Box>
    </ThemeProvider>
  );
}